import { gql } from "@apollo/client";

// Add OneSignal subscription mutation
export const ADD_ONESIGNAL_SUBSCRIPTION_MUTATION = gql`
	mutation AddOneSignalSubscription($params: OneSignalInputParams) {
		oneSignal(params: $params) {
			addSubscriptionId
		}
	}
`;

// Remove OneSignal subscription mutation
// export const REMOVE_ONESIGNAL_SUBSCRIPTION_MUTATION = gql`
// 	mutation RemoveOneSignalSubscription($playerId: String!) {
// 		removeOneSignalSubscription(playerId: $playerId) {
// 			success
// 			message
// 		}
// 	}
// `;

// Get user's OneSignal subscriptions query
// export const GET_ONESIGNAL_SUBSCRIPTIONS_QUERY = gql`
// 	query GetOneSignalSubscriptions($userId: String!) {
// 		oneSignalSubscriptions(userId: $userId) {
// 			playerId
// 			appId
// 			createdAt
// 			active
// 		}
// 	}
// `;

// TypeScript interfaces for OneSignal operations
export interface AddOneSignalSubscriptionParams {
	oneSignalPlayerId: string;
	oneSignalAppId: string;
}

export interface OneSignalInputParams {
	addSubscriptionId: AddOneSignalSubscriptionParams;
}

export interface OneSignalMutationResponse {
	addSubscriptionId: boolean;
}

export interface OneSignalSubscriptionParams {
	oneSignalPlayerId: string;
	oneSignalAppId: string;
	userId?: string;
}

export interface OneSignalSubscriptionResponse {
	success: boolean;
	message: string;
	playerId?: string;
}

export interface OneSignalSubscription {
	playerId: string;
	appId: string;
	createdAt: string;
	active: boolean;
}

export interface OneSignalSubscriptionsResponse {
	oneSignalSubscriptions: OneSignalSubscription[];
}

// Local storage helper functions for temporary storage
export const OneSignalLocalStorage = {
	// Store subscription locally until server supports it
	storeSubscription: (params: OneSignalSubscriptionParams & { userId: string }) => {
		const subscriptions = OneSignalLocalStorage.getSubscriptions();
		const existingIndex = subscriptions.findIndex(
			sub => sub.oneSignalPlayerId === params.oneSignalPlayerId && sub.userId === params.userId,
		);

		const subscription = {
			...params,
			timestamp: new Date().toISOString(),
			synced: false,
		};

		if (existingIndex >= 0) {
			subscriptions[existingIndex] = subscription;
		} else {
			subscriptions.push(subscription);
		}

		localStorage.setItem("oneSignalSubscriptions", JSON.stringify(subscriptions));
		return subscription;
	},

	// Get all stored subscriptions
	getSubscriptions: (): Array<OneSignalSubscriptionParams & { userId: string; timestamp: string; synced: boolean }> => {
		if (typeof window === "undefined") return [];

		try {
			const stored = localStorage.getItem("oneSignalSubscriptions");
			return stored ? JSON.parse(stored) : [];
		} catch {
			return [];
		}
	},

	// Get subscriptions for a specific user
	getUserSubscriptions: (userId: string) => {
		return OneSignalLocalStorage.getSubscriptions().filter(sub => sub.userId === userId);
	},

	// Mark subscription as synced with server
	markAsSynced: (playerId: string, userId: string) => {
		const subscriptions = OneSignalLocalStorage.getSubscriptions();
		const subscription = subscriptions.find(sub => sub.oneSignalPlayerId === playerId && sub.userId === userId);

		if (subscription) {
			subscription.synced = true;
			localStorage.setItem("oneSignalSubscriptions", JSON.stringify(subscriptions));
		}
	},

	// Remove subscription from local storage
	removeSubscription: (playerId: string, userId: string) => {
		const subscriptions = OneSignalLocalStorage.getSubscriptions();
		const filtered = subscriptions.filter(sub => !(sub.oneSignalPlayerId === playerId && sub.userId === userId));
		localStorage.setItem("oneSignalSubscriptions", JSON.stringify(filtered));
	},

	// Clear all subscriptions
	clearAll: () => {
		localStorage.removeItem("oneSignalSubscriptions");
	},

	// Get unsynced subscriptions (for batch sync when server is ready)
	getUnsyncedSubscriptions: () => {
		return OneSignalLocalStorage.getSubscriptions().filter(sub => !sub.synced);
	},
};
